/**
 * JSON Tab Component
 * Direct JSON schema editing with validation
 * Phase 4: Enhanced with detailed error messages and line/column indicators
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useUIStore } from '@atlas-forms/state-react';
import type { FormSchema } from '@atlas-forms/types-js';
import { CheckIcon, CloseIcon } from '@atlas-forms/ui-components-react';
import '../../styles/tabs/JsonTab.css';

export interface JsonTabProps {
  schema: FormSchema;
  onSchemaChange: (schema: FormSchema) => void;
}

interface ValidationError {
  message: string;
  line?: number;
  column?: number;
  type: 'syntax' | 'schema' | 'application';
}

/**
 * Parse JSON syntax error to extract line/column information
 */
function parseJsonError(error: Error): ValidationError {
  const message = error.message;

  // Try to extract line/column from standard JSON.parse errors
  // Example: "Unexpected token } in JSON at position 42"
  // Example: "JSON.parse: expected property name or '}' at line 5 column 3"
  const lineMatch = message.match(/line (\d+)/i);
  const columnMatch = message.match(/column (\d+)/i);
  const positionMatch = message.match(/position (\d+)/i);

  const result: ValidationError = {
    message,
    type: 'syntax',
  };

  if (lineMatch) {
    result.line = parseInt(lineMatch[1], 10);
  }
  if (columnMatch) {
    result.column = parseInt(columnMatch[1], 10);
  }

  // If we only have position, try to calculate line/column
  if (positionMatch && !lineMatch) {
    // This is approximate and may not be perfect
    result.message = `${message} (Check syntax near character ${positionMatch[1]})`;
  }

  return result;
}

/**
 * JSON Tab - Direct schema JSON editing
 */
export const JsonTab: React.FC<JsonTabProps> = ({ schema, onSchemaChange }) => {
  const { theme } = useUIStore();
  const [jsonText, setJsonText] = useState(JSON.stringify(schema, null, 2));
  const [isValid, setIsValid] = useState(true);
  const [validationError, setValidationError] = useState<ValidationError | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // Sync external schema changes into local text (e.g. when form data loads async).
  // Skip if the user is mid-edit (isValid=false means they've typed something invalid).
  const prevSchemaRef = useRef(schema);
  useEffect(() => {
    if (schema !== prevSchemaRef.current) {
      prevSchemaRef.current = schema;
      if (isValid) {
        setJsonText(JSON.stringify(schema, null, 2));
      }
    }
  }, [schema, isValid]);

  const validateAndUpdate = useCallback(
    (text: string) => {
      try {
        const parsed = JSON.parse(text);

        // Basic schema validation with detailed error messages
        if (!parsed.metadata) {
          const error: ValidationError = {
            message: 'Missing required field: "metadata". The schema must contain form metadata.',
            type: 'schema',
          };
          setJsonText(text);
          setIsValid(false);
          setValidationError(error);
          return;
        }

        if (!Array.isArray(parsed.controls)) {
          const error: ValidationError = {
            message: 'Missing or invalid required field: "controls". The schema must contain a controls array.',
            type: 'schema',
          };
          setJsonText(text);
          setIsValid(false);
          setValidationError(error);
          return;
        }

        // Validate control structure
        for (let i = 0; i < parsed.controls.length; i++) {
          const control = parsed.controls[i];
          if (!control.id) {
            const error: ValidationError = {
              message: `Control at index ${i} is missing required field: "id"`,
              type: 'schema',
            };
            setJsonText(text);
            setIsValid(false);
            setValidationError(error);
            return;
          }
          if (!control.type) {
            const error: ValidationError = {
              message: `Control "${control.id}" at index ${i} is missing required field: "type"`,
              type: 'schema',
            };
            setJsonText(text);
            setIsValid(false);
            setValidationError(error);
            return;
          }
        }

        setJsonText(text);
        setIsValid(true);
        setValidationError(null);

        // Try to apply the changes
        try {
          onSchemaChange(parsed);
        } catch (e) {
          const error: ValidationError = {
            message: e instanceof Error ? e.message : 'Failed to apply schema changes',
            type: 'application',
          };
          setValidationError(error);
        }
      } catch (e) {
        setJsonText(text);
        setIsValid(false);
        const parsedError = parseJsonError(e instanceof Error ? e : new Error('Invalid JSON'));
        setValidationError(parsedError);
      }
    },
    [onSchemaChange]
  );

  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonText(formatted);
      setIsValid(true);
      setValidationError(null);
    } catch (e) {
      const error = parseJsonError(e instanceof Error ? e : new Error('Invalid JSON - cannot format'));
      setValidationError(error);
    }
  }, [jsonText]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(jsonText);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  }, [jsonText]);

  const handleReset = useCallback(() => {
    const resetText = JSON.stringify(schema, null, 2);
    setJsonText(resetText);
    setIsValid(true);
    setValidationError(null);
  }, [schema]);

  return (
    <div className="af-json-tab" data-theme={theme}>
      <div className="af-json-tab__toolbar">
        <button
          onClick={handleFormat}
          className="af-json-tab__btn"
          title="Format JSON"
        >
          Format
        </button>
        <button
          onClick={handleCopy}
          className="af-json-tab__btn"
        >
          {isCopied ? 'Copied!' : 'Copy'}
        </button>
        <button
          onClick={handleReset}
          className="af-json-tab__btn"
          title="Reset to last saved"
        >
          Reset
        </button>
        <div className="af-json-tab__status">
          {isValid ? (
            <span className="af-json-tab__valid"><CheckIcon width={14} height={14} /> Valid JSON</span>
          ) : (
            <span className="af-json-tab__invalid"><CloseIcon width={14} height={14} /> Invalid JSON</span>
          )}
        </div>
      </div>

      <div className={`af-json-tab__editor ${isValid ? 'af-json-tab__editor--valid' : 'af-json-tab__editor--invalid'}`}>
        <textarea
          value={jsonText}
          onChange={(e) => validateAndUpdate(e.target.value)}
          className="af-json-tab__textarea"
          spellCheck="false"
          aria-label="JSON Schema Editor"
          aria-invalid={!isValid}
          aria-describedby={validationError ? 'json-error-message' : undefined}
        />
      </div>

      {validationError && (
        <div
          id="json-error-message"
          className={`af-json-tab__error af-json-tab__error--${validationError.type}`}
          role="alert"
          aria-live="polite"
        >
          <div className="af-json-tab__error-header">
            <span className={`af-json-tab__error-badge af-json-tab__error-badge--${validationError.type}`}>
              {validationError.type === 'syntax' && 'Syntax Error'}
              {validationError.type === 'schema' && 'Schema Error'}
              {validationError.type === 'application' && 'Application Error'}
            </span>
            {validationError.line && (
              <span className="af-json-tab__error-location">
                Line {validationError.line}
                {validationError.column && `, Column ${validationError.column}`}
              </span>
            )}
          </div>
          <p className="af-json-tab__error-message">{validationError.message}</p>

          {validationError.type === 'syntax' && (
            <div className="af-json-tab__error-hint">
              <strong>Tip:</strong> Common issues include missing commas, unmatched brackets, or trailing commas in JSON.
            </div>
          )}
          {validationError.type === 'schema' && (
            <div className="af-json-tab__error-hint">
              <strong>Tip:</strong> Check that all required fields (metadata, controls) are present and properly formatted.
            </div>
          )}
        </div>
      )}

      <div className="af-json-tab__info">
        <h4>JSON Schema Format</h4>
        <p>
          Edit the form schema directly as JSON. The schema must contain:
        </p>
        <ul>
          <li><code>metadata</code> - Form metadata (title, description, etc.)</li>
          <li><code>controls</code> - Array of form controls</li>
          <li><code>sections</code> - Optional array of form sections</li>
          <li><code>version</code> - Schema version for compatibility</li>
        </ul>
        <p>
          Changes are automatically applied when you edit the JSON, provided it remains valid.
        </p>
      </div>
    </div>
  );
};

JsonTab.displayName = 'JsonTab';
